import { useCallback, useEffect, useRef, useState } from 'react'
import { ModalWrapper, ModalContent, ModalBody } from './styles'
import { Modal as MaterialModal, Slide } from '@material-ui/core'
import { LoadingIndicator } from 'components/Indicator/LoadingIndicator'
import { zIndexes } from 'constants/zindex'
import { Fade } from 'components/Fade'
import dynamic from 'next/dynamic'
import { Wrapper } from 'styles/styleSystem/wrapper'

const ExitConfirmModal = dynamic(() =>
  import('./ExitConfirmModal').then((res) => res.ExitConfirmModal)
)
const ModalHeader = dynamic(() =>
  import('../ModalHeader').then((res) => res.ModalHeader)
)
const ModalFooter = dynamic(() =>
  import('../ModalFooter').then((res) => res.ModalFooter)
)

interface ModalProps {
  size?: string
  title?: string
  showHeader?: boolean
  okButtonText?: string
  onOk?: any
  onClose?: any
  isOpen?: boolean
  noCancel?: boolean
  noFooter?: boolean
  noPadding?: boolean
  options?: any
  loading?: boolean
  okBtnLoading?: boolean
  onClearAll?: () => void
  fitContent?: boolean
  alignRight?: boolean
  isAllowedFullscreen?: boolean
  showExitAlert?: boolean
  isFullscreen?: boolean
  onToggleFullscreen?: () => void
  onDisableFullscreen?: () => void
  showFullscreenToggle?: boolean
  minWidth?: string | number
  maxWidth?: string | number
  completeByEnterKey?: boolean
  isOkBtnDisabled?: boolean
  closeOnOutsideClick?: boolean
  onCloseModalFunctionGetter?: (onCloseModalFunction: () => void) => void
  isUsingLoggedInAction?: boolean
  zIndex?: typeof zIndexes.modal
  children?: React.ReactNode
}

export const Modal: React.FC<ModalProps> = ({
  size = 'md',
  title = 'Modal Title',
  showHeader = true,
  okButtonText = 'Save',
  onOk = null,
  onClose = null,
  isOpen = false,
  noCancel = false,
  noFooter = false,
  noPadding = false,
  options = null,
  loading = false,
  okBtnLoading = false,
  onClearAll = null,
  fitContent = false,
  alignRight = true,
  isAllowedFullscreen = false,
  showExitAlert = false,
  isFullscreen = false,
  onToggleFullscreen = undefined,
  onDisableFullscreen = undefined,
  showFullscreenToggle = true,
  minWidth = undefined,
  maxWidth = undefined,
  completeByEnterKey = true,
  isOkBtnDisabled = false,
  closeOnOutsideClick = true,
  onCloseModalFunctionGetter = null,
  isUsingLoggedInAction = false,
  zIndex = zIndexes.modal,
  children,
}) => {
  const [exitModalOpen, setExitModalOpen] = useState(false)
  const bodyRef = useRef(null)

  const closeModal = useCallback(() => {
    if (showExitAlert) {
      setExitModalOpen(true)
      return
    }
    onDisableFullscreen?.()
    onClose?.()
  }, [onClose, setExitModalOpen])

  useEffect(() => {
    onCloseModalFunctionGetter?.(closeModal)
  }, [onCloseModalFunctionGetter])

  const closeModalFromExitModal = () => {
    setExitModalOpen(false)
    onDisableFullscreen?.()
    onClose?.()
  }

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0
    }
  }, [isOpen])

  return (
    <>
      {showExitAlert && (
        <ExitConfirmModal
          isOpen={exitModalOpen}
          onCloseModal={() => setExitModalOpen(false)}
          onExit={closeModalFromExitModal}
        />
      )}
      <MaterialModal open={isOpen} onClose={closeModal} style={{ zIndex }}>
        <Slide direction="up" in={isOpen} timeout={300}>
          <div>
            <Fade noFadeOut>
              <ModalWrapper
                onClick={() => (closeOnOutsideClick ? closeModal() : null)}
                isFullscreen={isFullscreen}
              >
                <ModalContent
                  minWidth={minWidth}
                  maxWidth={maxWidth}
                  size={size}
                  onClick={(e) => e.stopPropagation()}
                  fitContent={fitContent}
                  isFullscreen={isFullscreen}
                >
                  {showHeader && (
                    <ModalHeader
                      title={title}
                      onClose={closeModal}
                      onClearAll={onClearAll}
                      onToggleFullscreen={onToggleFullscreen}
                      showFullscreenToggle={
                        isAllowedFullscreen && showFullscreenToggle
                      }
                      isFullscreen={isFullscreen}
                    />
                  )}
                  <ModalBody
                    ref={bodyRef}
                    noPadding={noPadding}
                    fitContent={fitContent}
                    isFullscreen={isFullscreen}
                  >
                    {loading ? (
                      <Wrapper
                        minHeight="300px"
                        height="100%"
                        display="flex"
                        alignItems="center"
                      >
                        <LoadingIndicator />
                      </Wrapper>
                    ) : (
                      children
                    )}
                  </ModalBody>
                  {!noFooter && (
                    <ModalFooter
                      onCancel={closeModal}
                      onOk={onOk}
                      okButtonText={okButtonText}
                      noCancel={noCancel}
                      options={options}
                      loading={okBtnLoading}
                      onClearAll={onClearAll}
                      alignRight={alignRight}
                      completeByEnterKey={completeByEnterKey}
                      isOkBtnDisabled={isOkBtnDisabled}
                      isUsingLoggedInAction={isUsingLoggedInAction}
                    />
                  )}
                </ModalContent>
              </ModalWrapper>
            </Fade>
          </div>
        </Slide>
      </MaterialModal>
    </>
  )
}
